import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllStockDumpReportData,
  getStockDumpReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { stockMovementTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./StockDump.css";

const tableHdrs = [
  "productName",
  "qty",
  "averageDailySales",
  "grossMargin",
  "movementType",
  "averagePurchasePrice",
  "averageSellingPrice",
  "unitsSold",
  "unitsPurchased",
  "grossMarginPercentage",
  "tradeName",
  "category",
  "subCategory",
  "hsnCode",
  "barcodeNo",
  "brand",
  "reorderLevel",
];
const initSearch = {
  productName: "",
  movementType: [],
  category: [],
  subcategory: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Qty",
  "Avg. Daily Sales",
  "Gross Margin",
  "Avg. Purchase Price",
  "Avg. Selling Price",
  "Units Sold",
  "Units Purchased",
  "Gross Margin Percentage",
  "Trade Name",
  "HSN Code",
  "Barcode Number",
  "Brand",
  "Reorder Level",
];

const StockDumpReport = () => {
  const dispatch = useDispatch();
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { stockDump, stockDumpAll } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllStockDumpReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    selectedWarehouse,
    selectedCategoryFilter,
    searchValues?.productName,
    searchValues?.movementType,
    searchValues?.category,
    searchValues?.subcategory,
  ]);

  const overviewData = stockDump?.overview || stockDump?.data || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "SKUs in stock",
      value:
        cards?.skusInStock?.display ??
        (cards?.skusInStock?.value !== undefined ? `${cards.skusInStock.value}` : "4,483"),
    },
    {
      title: "Stock value",
      value:
        cards?.stockValue?.display ??
        (cards?.stockValue?.value !== undefined ? `₹${(cards.stockValue.value / 100000).toFixed(1)}L` : "₹68.9L"),
    },
    {
      title: "Near expiry",
      value:
        cards?.nearExpiry?.display ??
        (cards?.nearExpiry?.value !== undefined ? `${cards.nearExpiry.value}` : "0"),
    },
    {
      title: "Low stock",
      value:
        cards?.lowStock?.display ??
        (cards?.lowStock?.value !== undefined ? `${cards.lowStock.value}` : "13"),
    },
  ];

  // Chart 1: Stock by category (Bar Chart)
  const rawStockCategory = Array.isArray(overviewData?.stockByCategory)
    ? overviewData.stockByCategory
    : [];

  const categoryNames =
    rawStockCategory.length > 0
      ? rawStockCategory.map((item) => item?.category || "Other")
      : [
          "Fluid Therapy", "Deworming", "Nutrition", "Flea & Tick",
          "Grooming & Hygiene", "Vaccines", "Cleaning", "Accessories & Toys", "Diet", "Mortuary",
        ];

  const categoryValues =
    rawStockCategory.length > 0
      ? rawStockCategory.map((item) => (typeof item?.skus === "number" && !isNaN(item.skus) ? item.skus : typeof item?.qty === "number" && !isNaN(item.qty) ? item.qty : 0))
      : [110, 100, 90, 115, 870, 110, 100, 620, 500, 30];

  const categoryChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "45%", borderRadius: 3 } },
    colors: ["#8B5CF6"],
    xaxis: {
      categories: categoryNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const categoryChartSeries = [
    { name: "SKUs", data: categoryValues },
  ];

  // Chart 2: Fast movers (Horizontal Bar Chart)
  const rawFastMovers = Array.isArray(overviewData?.fastSlowMovers?.fastMovers)
    ? overviewData.fastSlowMovers.fastMovers
    : [];

  const fastMoversNames =
    rawFastMovers.length > 0
      ? rawFastMovers.map((item) => item?.tradeName || item?.productName || "Product")
      : ["Iron and calcium trade a...", "Saline", "Stock WO batch6", "Stock WO batch5", "Jul 29", "Test Stock12"];

  const fastMoversValues =
    rawFastMovers.length > 0
      ? rawFastMovers.map((item) => (typeof item?.unitsSold === "number" && !isNaN(item.unitsSold) ? item.unitsSold : typeof item?.salesValue === "number" && !isNaN(item.salesValue) ? item.salesValue : 0))
      : [90, 95, 100, 110, 200, 920];

  const fastMoversChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 3 } },
    colors: ["#2563EB"],
    xaxis: {
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      categories: fastMoversNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const fastMoversChartSeries = [
    { name: "Units", data: fastMoversValues },
  ];

  // Chart 3: Slow movers (Horizontal Bar Chart)
  const rawSlowMovers = Array.isArray(overviewData?.fastSlowMovers?.slowMovers)
    ? overviewData.fastSlowMovers.slowMovers
    : [];

  const slowMoversNames =
    rawSlowMovers.length > 0
      ? rawSlowMovers.map((item) => item?.tradeName || item?.productName || "Product")
      : ["New Stock Test2", "Defensor 3", "Benzoyl Peroxide Shamp...", "SYNOQUIN", "Fluralaner Chew 112.5mg"];

  const slowMoversValues =
    rawSlowMovers.length > 0
      ? rawSlowMovers.map((item) => (typeof item?.subunits === "number" && !isNaN(item.subunits) ? item.subunits : typeof item?.stockValue === "number" && !isNaN(item.stockValue) ? item.stockValue : 0))
      : [0.4, 0.8, 1.0, 1.5, 2.0];

  const slowMoversChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 3 } },
    colors: ["#EF4444"],
    xaxis: {
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      categories: slowMoversNames,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const slowMoversChartSeries = [
    { name: "Units in Stock", data: slowMoversValues },
  ];

  // Chart 4: Stock turnover (Line Chart)
  const rawTurnover = Array.isArray(overviewData?.stockTurnover)
    ? overviewData.stockTurnover
    : [];

  const turnoverCategories =
    rawTurnover.length > 0
      ? rawTurnover.map((item) => item?.month || "")
      : ["Sep 2026", "Oct 2025", "Nov 2025", "Dec 2025", "Jan 2026", "Feb 2026", "Mar 2026", "Apr 2026", "May 2026", "Jun 2026", "Jul 2026", "Aug 2026"];

  const turnoverValues =
    rawTurnover.length > 0
      ? rawTurnover.map((item) => (typeof item?.turnover === "number" && !isNaN(item.turnover) ? item.turnover : typeof item?.cogs === "number" && !isNaN(item.cogs) ? item.cogs : 0))
      : [50, 60, 70, 80, 100, 150, 500, 1450, 950, 450, 150, 50];

  const turnoverChartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    colors: ["#2563EB"],
    xaxis: {
      categories: turnoverCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const turnoverChartSeries = [
    { name: "Turnover Rate", data: turnoverValues },
  ];

  // Chart 5: Price & margin watch (Horizontal Bar Chart)
  const rawPriceMarginWatch = Array.isArray(overviewData?.priceMarginWatch)
    ? overviewData.priceMarginWatch
    : [];

  const marginWatchCategories =
    rawPriceMarginWatch.length > 0
      ? rawPriceMarginWatch.map(
          (item) => item?.productName || item?.tradeName || "Product"
        )
      : [
          "Oralade GI Rehydration",
          "Test Stock12",
          "Stock WO batch1",
          "Fluralaner Chew 112.5mg",
          "SYNOQUIN",
        ];

  const marginWatchValues =
    rawPriceMarginWatch.length > 0
      ? rawPriceMarginWatch.map((item) =>
          typeof item?.grossMarginPercent === "number" && !isNaN(item.grossMarginPercent)
            ? Number(item.grossMarginPercent.toFixed(1))
            : typeof item?.netMarginPercent === "number" && !isNaN(item.netMarginPercent)
            ? Number(item.netMarginPercent.toFixed(1))
            : 0
        )
      : [-2746.7, -80, -60, -40, 20];

  const marginWatchChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, barHeight: "45%", borderRadius: 3 } },
    colors: [
      ({ value }) => (value < 0 ? "#EF4444" : "#10B981"),
    ],
    xaxis: {
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      categories: marginWatchCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const marginWatchChartSeries = [
    { name: "Margin %", data: marginWatchValues },
  ];

  const handleExportExcel = () => {
    const tableData = stockDump?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Product Name": row?.productName ?? "",
      QTY: row?.qty ?? 0,
      "Avg. Daily Sales": row?.averageDailySales ?? 0,
      "Gross Margin": row?.grossMargin ?? 0,
      "Movement Type": row?.movementType ?? "",
      Category: row?.category ?? "",
      Brand: row?.brand ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Overview");
    XLSX.writeFile(workbook, `Stock_Overview_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { type: "dump", page: currentPage, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (selectedWarehouse !== "all") reqObj.warehouse = selectedWarehouse;
    if (selectedCategoryFilter !== "all") reqObj.category = selectedCategoryFilter;
    if (searchValues?.productName?.length > 0) reqObj.productName = [searchValues?.productName];
    if (searchValues?.movementType?.length > 0) reqObj.movementType = searchValues?.movementType?.map((li) => li?.value);
    if (searchValues?.category?.length > 0) reqObj.category = searchValues?.category?.map((li) => li?.value);
    if (searchValues?.subcategory?.length > 0) reqObj.subcategory = searchValues?.subcategory?.map((li) => li?.value);
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getStockDumpReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((stockDump?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (stockDump?.data ?? [])?.map((li) => ({
      ...li,
      productName: li?.productName ?? "",
      qty: li?.qty ?? 0,
      averageDailySales: Number(li?.averageDailySales ?? 0)?.toFixed(2),
      grossMargin: Number(li?.grossMargin ?? 0)?.toFixed(2),
      movementType: li?.movementType ?? "",
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getProductNameList = () => uniq((stockDump?.data ?? [])?.map((li) => li?.productName)?.filter(Boolean));

  const getDropListByType = (typ) => {
    return typ === "Movement Type" ? stockMovementTypes : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="stock-dump-container">
      {/* Title */}
      <h2 className="stock-dump-title">Stock overview</h2>

      {/* Top Filter Bar */}
      <div className="stock-dump-filter-bar">
        <Select
          size="small"
          value={selectedWarehouse}
          onChange={(e) => setSelectedWarehouse(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">Warehouse</MenuItem>
        </Select>

        <Select
          size="small"
          value={selectedCategoryFilter}
          onChange={(e) => setSelectedCategoryFilter(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All categories</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="stock-dump-export-group">
        <button type="button" className="stock-dump-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="stock-dump-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (4 Cards) */}
      <div className="stock-dump-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="stock-dump-card" key={idx}>
            <h4 className="stock-dump-card-title">{card.title}</h4>
            <div className="stock-dump-card-value-container">
              <h2 className="stock-dump-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Bar Chart: Stock by category */}
      <div className="stock-dump-chart-card">
        <div className="stock-dump-chart-header">
          Stock by category
        </div>
        <Chart
          options={categoryChartOptions}
          series={categoryChartSeries}
          type="bar"
          height={300}
        />
      </div>

      {/* Side-by-side Grid: Fast movers + Slow movers */}
      <div className="stock-dump-charts-grid">
        <div className="stock-dump-chart-card">
          <div className="stock-dump-chart-header">
            Fast movers
          </div>
          <Chart
            options={fastMoversChartOptions}
            series={fastMoversChartSeries}
            type="bar"
            height={280}
          />
        </div>

        <div className="stock-dump-chart-card">
          <div className="stock-dump-chart-header">
            Slow movers
          </div>
          <Chart
            options={slowMoversChartOptions}
            series={slowMoversChartSeries}
            type="bar"
            height={280}
          />
        </div>
      </div>

      {/* Full-width Line Chart: Stock turnover */}
      <div className="stock-dump-chart-card">
        <div className="stock-dump-chart-header">
          Stock turnover
        </div>
        <Chart
          options={turnoverChartOptions}
          series={turnoverChartSeries}
          type="line"
          height={300}
        />
      </div>

      {/* Full-width Chart: Price & margin watch */}
      <div className="stock-dump-chart-card">
        <div className="stock-dump-chart-header">
          Price & margin watch
        </div>
        <div className="stock-dump-chart-subheader">
          Flags products with negative or thin gross margin.
        </div>
        <Chart
          options={marginWatchChartOptions}
          series={marginWatchChartSeries}
          type="bar"
          height={280}
        />
      </div>

      {/* Drill-down Section */}
      <div className="stock-dump-drilldown-header">Drill-down — products</div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getProductNameList()}
              value={searchValues?.productName ?? []}
              name="productName"
              handleChange={onSearchChange}
              label="Product Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={stockMovementTypes}
              value={searchValues?.movementType ?? []}
              name="movementType"
              handleChange={onSearchChange}
              label="Movement Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {stockDump?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((stockDump?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      <CustomTextField
                        placeholder={li}
                        fullWidth
                        name={li}
                        handleChange={onFilterValuesChange}
                        value={filterValues?.values?.[li] ?? ""}
                        label={li}
                      />
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default StockDumpReport;
